import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Users, Eye, MessageSquare, ThumbsUp, ExternalLink, Copy, RefreshCw, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";

/**
 * ReviewersDialog — shows everyone invited to review this item, with live status:
 *   opened_at  • comments_count  • approved_at
 *
 * UX rules:
 *  - On every open (and on explicit refresh) we re-fetch in the background.
 *  - Rows from a previous open render with a subtle "stale" treatment until
 *    the fresh fetch lands, so users never see ambiguous data without an
 *    indicator.
 *  - "Last refreshed" timestamp shows users when the data was loaded.
 */
export default function ReviewersDialog({ itemId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [stale, setStale] = useState(false);
  const reqIdRef = useRef(0);

  const load = async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    try {
      const { data } = await api.get(`/review-items/${itemId}/reviewers`);
      // Ignore stale responses if a newer fetch was kicked off mid-flight
      if (myReq !== reqIdRef.current) return;
      setRows(data);
      setLastRefreshed(new Date().toISOString());
      setStale(false);
    } catch {
      if (myReq === reqIdRef.current) setStale(true);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open, itemId]);

  const handleOpenChange = (nextOpen) => {
    if (nextOpen) {
      // Mark current rows stale immediately so users see the indicator on reopen.
      setStale(true);
    }
    setOpen(nextOpen);
    if (nextOpen) load();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none h-8" data-testid="reviewers-open-btn">
          <Users className="w-3.5 h-3.5 mr-1.5" /> Reviewers
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Reviewers — accountability dashboard</DialogTitle>
          <DialogDescription>
            Who&apos;s been invited, who&apos;s opened, commented, or approved. Create personalized invites from the Share dialog.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between text-[10px] text-muted-foreground border-b border-border pb-2" data-testid="reviewers-status-row">
          <div className="inline-flex items-center gap-2">
            {loading ? (
              <span className="inline-flex items-center gap-1.5" data-testid="reviewers-loading">
                <Loader2 className="w-3 h-3 animate-spin" /> Refreshing…
              </span>
            ) : lastRefreshed ? (
              <span data-testid="reviewers-last-refresh">Last refreshed {fmtRelative(lastRefreshed)}</span>
            ) : (
              <span>Awaiting first load…</span>
            )}
            {stale && !loading && (
              <span className="chip chip-pending text-[9px]" data-testid="reviewers-stale-badge">stale</span>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={load}
            disabled={loading}
            className="rounded-none h-7 px-2 text-[10px]"
            data-testid="reviewers-refresh-btn"
            title="Refresh reviewers list"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <div className={`space-y-2 ${stale && !loading ? "opacity-70" : ""}`}>
          {rows.length === 0 && !loading && (
            <div className="text-sm text-muted-foreground py-6 text-center" data-testid="reviewers-empty">
              No personalized invites yet. Open Share → Invites tab to create one.
            </div>
          )}
          <table className="w-full text-xs" data-testid="reviewers-table">
            {rows.length > 0 && (
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 pr-2 label-eyebrow">Reviewer</th>
                  <th className="py-2 pr-2 label-eyebrow">Opened</th>
                  <th className="py-2 pr-2 label-eyebrow text-center">Comments</th>
                  <th className="py-2 pr-2 label-eyebrow">Approved</th>
                  <th className="py-2 pr-2 label-eyebrow">Link</th>
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((r) => {
                const url = r.invite_url || "";
                const status = r.has_approved
                  ? "approved"
                  : (r.comments_count > 0 ? "commented" : (r.last_seen_at ? "opened" : "pending"));
                return (
                  <tr key={r.id} className="border-b border-border" data-testid={`reviewer-row-${r.id}`}>
                    <td className="py-2 pr-2 align-top">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-muted-foreground font-mono text-[10px]">{r.email}</div>
                      <div className={`chip text-[9px] mt-1 ${status === "approved" ? "chip-approved" : (status === "commented" ? "chip-client" : (status === "opened" ? "chip-open" : ""))}`} data-testid={`reviewer-status-${r.id}`}>{status}</div>
                    </td>
                    <td className="py-2 pr-2 align-top">
                      {r.first_seen_at ? <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {fmtRelative(r.first_seen_at)}</span> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-2 align-top text-center">
                      <span className="inline-flex items-center gap-1" data-testid={`reviewer-comments-${r.id}`}><MessageSquare className="w-3 h-3" /> {r.comments_count || 0}</span>
                    </td>
                    <td className="py-2 pr-2 align-top">
                      {r.has_approved ? <span className="inline-flex items-center gap-1 text-primary"><ThumbsUp className="w-3 h-3" /> {r.approved_at ? fmtRelative(r.approved_at) : "yes"}</span> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-2 pr-2 align-top">
                      <div className="flex items-center gap-1">
                        <button onClick={() => { navigator.clipboard.writeText(url); toast.success("Personalized link copied"); }} className="text-primary hover:underline" data-testid={`reviewer-copy-${r.id}`} title="Copy link">
                          <Copy className="w-3 h-3" />
                        </button>
                        <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`reviewer-open-${r.id}`} title="Open in new tab">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                        <ResendButton inviteId={r.id} disabled={r.has_approved} reviewer={r} onResent={load} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResendButton({ inviteId, disabled, reviewer, onResent }) {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (disabled) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/reviewer-invites/${inviteId}/resend`);
      if (data?.sent) toast.success(`Resent invite to ${reviewer.email}`);
      else toast.info(`Logged — email provider may be in log-only mode`);
      onResent && onResent();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't resend"));
    } finally { setBusy(false); }
  };
  return (
    <button
      onClick={send}
      disabled={disabled || busy}
      className={`hover:text-primary ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
      title={disabled ? "Already approved — no resend needed" : "Resend personalized invite email"}
      data-testid={`reviewer-resend-${inviteId}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
    </button>
  );
}
